import { SelectionFieldDefinition } from '@fsx/ui-components';
import {
  FilingProfile,
  CaseRequestViewModel,
  CasePartyViewModel,
  ParticipantSpec,
  IValidatable,
  FilingModeSpec,
  FilingMode,
  CaseParty,
} from '../../../types';
import {
  FsxAdditionalFieldsValidationService,
  IAdditionalFieldsValidationService,
} from './additional-fields-validation.service';
import {
  FsxRepresentationValidationService,
  IRepresentationValidationService,
} from './representation-validation.service';
import {
  FsxValidationHelperService,
  IValidationHelperService,
} from './validation-helper.service';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import {
  FsxValidationErrorsService,
  IValidationErrorsService,
} from 'projects/apps/fsx-ui/src/app/filing-editor/services/validation-errors.service';
import { ValidationGroupConstants } from 'projects/apps/fsx-ui/src/app/filing-editor/services/validation-group-errors.service';

export const FsxPartyValidationService =
  new InjectionToken<IPartyValidationService>('FsxPartyValidationService');

export interface IPartyValidationService {
  validateParties(
    caseRequest: CaseRequestViewModel, // Needed to pass the parties that we want to validate
    modeSpec: FilingModeSpec, // Needed for the participantSpecs that we want to validate against
    filingProfile: FilingProfile // Only needed down stream by other validation services (Do they really need it?)
  ): boolean;

  validateParty(
    party: CasePartyViewModel,
    spec: ParticipantSpec,
    caseRequest: CaseRequestViewModel,
    filingProfile: FilingProfile,
    modeSpec: FilingModeSpec
  ): boolean;
}

@Injectable()
export class PartyValidationService implements IPartyValidationService {
  constructor(
    @Inject(FsxValidationHelperService)
    private readonly validationHelperService: IValidationHelperService,
    @Inject(FsxRepresentationValidationService)
    private readonly representationValidationService: IRepresentationValidationService,
    @Inject(FsxAdditionalFieldsValidationService)
    private readonly additionalFieldsValidationService: IAdditionalFieldsValidationService,
    @Inject(FsxValidationErrorsService)
    private readonly validationErrorsService: IValidationErrorsService
  ) {}

  public validateParties(
    caseRequest: CaseRequestViewModel,
    modeSpec: FilingModeSpec,
    filingProfile: FilingProfile
  ): boolean {
    let isPartiesValid = true;

    // Check against profile rules first...
    const caseRequestParties: CasePartyViewModel[] = caseRequest.parties || [];
    const participantSpecs: ParticipantSpec[] = modeSpec.participant || [];

    if (participantSpecs.length > 0) {
      if (
        !this.validatePartiesAgainstParticipantSpecs(
          caseRequestParties,
          participantSpecs
        )
      ) {
        caseRequest.isValid = false;
        isPartiesValid = false;
      }
    }

    if (
      !this.validatePartiesAgainstEachother(caseRequestParties, caseRequest)
    ) {
      caseRequest.isValid = false;
      isPartiesValid = false;
    }

    for (let party of caseRequestParties) {
      party.isValid = true;
      party.isRepresentationValid = true;

      if (!!party.efmKey) {
        // in a SubF, existing parties should not be validated
        continue;
      }

      if (!party.participantCategory?.name) {
        return this.validationHelperService.markItemAsInvalid(
          party,
          caseRequest
        );
      }

      const spec = participantSpecs.find(
        (s) => party.participantCategory?.name === s.participantCategory.name
      );

      if (!spec) {
        return this.validationHelperService.markItemAsInvalid(
          party,
          caseRequest
        );
      }

      if (
        !this.validateParty(party, spec, caseRequest, filingProfile, modeSpec)
      ) {
        return this.validationHelperService.markItemAsInvalid(
          party,
          caseRequest
        );
      }
    }
    return isPartiesValid;
  }

  private validatePartiesAgainstEachother(
    parties: CasePartyViewModel[],
    caseRequest: CaseRequestViewModel
  ): boolean {
    const seenParticipantNames: string[] = [];
    return parties.reduce(
      (accIsValid: boolean, curParty: CasePartyViewModel) => {
        if (seenParticipantNames.includes(curParty.participantName)) {
          accIsValid = false;
          this.validationHelperService.markItemAsInvalid(curParty, caseRequest);
        }
        seenParticipantNames.push(curParty.participantName);
        return accIsValid;
      },
      true
    );
  }

  private validatePartiesAgainstParticipantSpecs(
    parties: CaseParty[],
    participantSpecs: ParticipantSpec[]
  ): boolean {
    return participantSpecs.reduce(
      (acc: boolean, spec: ParticipantSpec, specIndex: number) => {
        const partiesForSpec: CasePartyViewModel[] = parties.filter(
          (party: CasePartyViewModel) => {
            return (
              spec.participantCategory.name === party.participantCategory?.name
            );
          }
        );

        const pSpecMinErrorCode: string = 'pSpecMin' + specIndex;
        const partyOrParties: string =
          spec.minRequired === 1 ? 'Party' : 'Parties';
        if (partiesForSpec.length < spec.minRequired) {
          acc = false;
          this.validationErrorsService.addValidationError({
            errorCode: pSpecMinErrorCode,
            errorMessage: `Must provide at least ${spec.minRequired} ${partyOrParties} of type ${spec.participantCategory.caption}`,
            group: ValidationGroupConstants.parties,
          });
        } else {
          this.validationErrorsService.removeValidationError(pSpecMinErrorCode);
        }

        const pSpecMaxErrorCode: string = 'pSpecMax' + specIndex;
        if (partiesForSpec.length > spec.maxAllowed) {
          acc = false;
          this.validationErrorsService.addValidationError({
            errorCode: pSpecMaxErrorCode,
            errorMessage: `Must not provide more than ${spec.maxAllowed} ${partyOrParties} of type ${spec.participantCategory.caption}`,
            group: ValidationGroupConstants.parties,
          });
        } else {
          this.validationErrorsService.removeValidationError(pSpecMaxErrorCode);
        }

        return acc;
      },
      true
    );
  }

  public validateParty(
    party: CasePartyViewModel,
    spec: ParticipantSpec,
    caseRequest: CaseRequestViewModel,
    filingProfile: FilingProfile,
    modeSpec: FilingModeSpec
  ): boolean {
    // party.isValid = true;

    if (!!party.efmKey) {
      // in a SubF, existing parties should not be validated
      return true;
    }

    if (
      !this.representationValidationService.validateRepresentations(
        party,
        spec.representation,
        caseRequest,
        filingProfile,
        modeSpec,
        caseRequest
      )
    ) {
      party.isRepresentationValid = false;
      return this.validationHelperService.markItemAsInvalid(party, caseRequest);
    }

    if (
      !this.validateSubCategories(
        party.participantSubCategoryNames,
        spec.subCategory,
        party,
        filingProfile
      )
    ) {
      return this.validationHelperService.markItemAsInvalid(party, caseRequest);
    }

    if (
      !this.additionalFieldsValidationService.validateAdditionalFields(
        party.additionalFieldValues,
        spec.additionalFields,
        party.caseId,
        caseRequest,
        filingProfile,
        caseRequest
      )
    ) {
      return this.validationHelperService.markItemAsInvalid(party, caseRequest);
    }

    return true;
  }

  private validateSubCategories(
    subCategoryNames: string[] | null | undefined,
    spec: SelectionFieldDefinition | null | undefined,
    scope: IValidatable,
    filingProfile: FilingProfile
  ): boolean {
    if (!spec) {
      return true;
    }

    const countOfNames = subCategoryNames?.length ?? 0;

    if (countOfNames < spec.minRequired) {
      return this.validationHelperService.markItemAsInvalid(scope, null);
    }

    if (countOfNames > spec.maxAllowed) {
      return this.validationHelperService.markItemAsInvalid(scope, null);
    }

    for (let subCategoryName in subCategoryNames) {
      this.validationHelperService.validateListReference(
        spec.listReference,
        subCategoryName,
        scope,
        filingProfile
      );
    }

    return true;
  }
}
